<template>
  <div class="relative w-[1200px] max-w-full">
    <Loading v-if="isSaving" class="absolute inset-0 z-10 bg-white/70">
      <p class="text-base text-gray-800">Saving...</p>
    </Loading>

    <FormKit v-model="values" type="form" :actions="false" :disabled="isSaving" @submit="save">
      <div class="grid gap-6 lg:grid-cols-[360px_1fr]">
        <section class="space-y-3">
          <h2 class="text-base font-bold">Client</h2>
          <div class="grid grid-cols-[100px_1fr] items-center gap-3">
            <FormLabel label="Name" required>
              <FormKit
                type="text"
                name="customer_name"
                validation="required"
                :validation-messages="{ required: 'Client name is required' }"
              />
            </FormLabel>
            <FormLabel label="Phone" required>
              <FormKit
                type="tel"
                name="phone"
                placeholder="+15551234567"
                :plugins="[phoneMask]"
                :validation="phoneValidation"
                :validation-messages="{
                  required: 'Phone number is required',
                  matches: 'Please enter a valid phone number, e.g. +15551234567',
                }"
              />
            </FormLabel>
            <FormLabel label="Email" required>
              <FormKit type="email" name="email" validation="required|email" />
            </FormLabel>
            <FormLabel label="Status">
              <FormKit type="select" name="status" :options="statusOptions" />
            </FormLabel>
          </div>
        </section>

        <section class="space-y-3">
          <h2 class="text-base font-bold">Canvas</h2>
          <PreviewCanvas
            ref="canvasRef"
            class="h-[520px]"
            mode="design"
            :url="canvas.url"
            :token="canvas.token"
            :scene="initialScene"
            @scene-changes="changes = $event"
          />
          <p
            v-if="changes.length"
            data-test="canvas-changes"
            class="rounded-lg border border-hqPurple300 bg-[#fbf7f8] px-4 py-2 text-sm"
          >
            <span class="font-medium text-gray-900">{{ changesSummary }}</span>
            <span class="text-gray-500"> — saved together with the rest.</span>
          </p>
        </section>
      </div>

      <div class="mt-6 flex justify-end border-t pt-4">
        <Button
          type="submit"
          data-test="save-edit"
          primaryButton
          leftIcon="check"
          leftIconColor="white"
          :text="isSaving ? 'Saving...' : 'Save'"
          :disabled="isSaving"
        />
      </div>
    </FormKit>
  </div>
</template>

<script setup>
import { computed, toRefs } from "vue";
import { FormKit } from "@formkit/vue";
import PreviewCanvas from "@/components/Previews/PreviewCanvas/PreviewCanvas.vue";
import Button from "@/components/UI/Form/Button/Button.vue";
import FormLabel from "@/components/UI/Form/FormLabel.vue";
import Loading from "@/components/UI/Loading/Loading.vue";
import { parseScene } from "@/utils/parseScene";
import { PHONE_PATTERN, phoneMask } from "@/utils/phoneMask";
import { describeSceneChanges } from "@/utils/sceneChanges";
import { useEditPrettyPreview } from "./useEditPrettyPreview";

const props = defineProps({
  // The pretty preview row being edited.
  preview: {
    type: Object,
    required: true,
  },
});

const { preview } = toRefs(props);
const { values, canvas, canvasRef, changes, isSaving, statusOptions, save } = useEditPrettyPreview(
  preview.value
);

// Array syntax: the string syntax splits rule arguments on commas, which would cut the
// pattern's {7,15} in half.
const phoneValidation = [["required"], ["matches", PHONE_PATTERN]];
const initialScene = parseScene(canvas.scene);
const changesSummary = computed(() => describeSceneChanges(changes.value));
</script>
